import { useRef } from 'react';

// ── SERVER-APPLIED UPDATES AND THE BLANKET-PERSIST EFFECTS ───────────
//
// Shared by the tournament and the series slices, which both persist on EVERY
// state change. Two copies of this would be two chances for one of them to
// stop suppressing and quietly start writing the server's own document back.
//
// The trio above writes on EVERY state change, which is what makes it safe:
// no handler can forget to persist. It is also exactly wrong for a change the
// SERVER made. `POST /tournament-results` applies the result, writes the
// document and bumps the row's version; if the effect then wrote this
// browser's copy back it would be (a) redundant, (b) carrying a stale
// `ifVersion` until the new one is adopted, and (c) the browser writing the
// session blob again — the very thing the command exists to stop.
//
// So a server-applied value is marked before it is set, and the effect skips
// that ONE value — by identity, not by a flag that could stay stuck on. The
// next ordinary change is a different object and persists as it always did.
// The R1 loaded-flag guard stays first and is untouched: this can only ever
// suppress a write, never enable one before the load has run.
export function useServerApplied<T>() {
  const applied = useRef<T | null>(null);
  // STABLE ACROSS RENDERS, and it has to be: this goes in the dependency array
  // of the blanket-persist effect. A fresh object literal each render would
  // re-run that effect on EVERY render — turning a write-on-change into a
  // write-on-render, against the client's own version precondition.
  const api = useRef({
    /** True once, for the exact value the server wrote. */
    consume(value: T): boolean {
      if (applied.current !== value || value == null) return false;
      applied.current = null;
      return true;
    },
    mark(value: T) {
      applied.current = value;
    },
  });
  return api.current;
}
